import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import api, { formatApiError } from "@/lib/api";
import { SECTORS } from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Search,
  Trash2,
  Building2,
  Mail,
  Phone,
  MapPin,
  Loader2,
  X,
  Pencil,
  Users,
  MapPinned,
  Filter,
  Boxes,
  History,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  contact_person: "",
  email: "",
  phone: "",
  address: "",
  sector: "Climatización",
  notes: "",
  lat: "",
  lng: "",
};

const ALL_SECTORS = "todos";

const EMPTY_ASSET = {
  name: "",
  asset_type: "Equipo",
  brand: "",
  model: "",
  serial_number: "",
  location: "",
  installation_date: "",
  warranty_until: "",
  status: "activo",
  notes: "",
};


function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="mantix-card flex items-center gap-4 p-5">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-2xl"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="h-5 w-5" style={{ color }} strokeWidth={1.8} />
      </div>
      <div>
        <p className="font-display text-2xl font-bold text-[#1D1D1F]">{value}</p>
        <p className="text-sm text-[#86868B]">{label}</p>
      </div>


    </div>
  );
}

function parseCoordinate(value) {
  if (value === "" || value == null) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [sectorFilter, setSectorFilter] = useState(ALL_SECTORS);
  const [open, setOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [assetOpen, setAssetOpen] = useState(false);
  const [assetClient, setAssetClient] = useState(null);
  const [assets, setAssets] = useState([]);
  const [assetLoading, setAssetLoading] = useState(false);
  const [assetSaving, setAssetSaving] = useState(false);
  const [assetDeletingId, setAssetDeletingId] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetForm, setAssetForm] = useState(EMPTY_ASSET);

  const loadClients = async () => {
    try {
      const { data } = await api.get("/clients");
      setClients(data || []);
    } catch (error) {
      toast.error(formatApiError(error) || "No se pudieron cargar los clientes.");
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await loadClients();
      setLoading(false);
    };
    load();
  }, []);

  const resetForm = () => {
    setForm(EMPTY);
    setEditingClient(null);
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setForm({
      name: client.name || "",
      contact_person: client.contact_person || "",
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
      sector: client.sector || "Climatización",
      notes: client.notes || "",
      lat: client.lat == null ? "" : String(client.lat),
      lng: client.lng == null ? "" : String(client.lng),
    });
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      toast.error("Escribe el nombre del cliente o empresa.");
      return;
    }

    const lat = parseCoordinate(form.lat);
    const lng = parseCoordinate(form.lng);

    if (form.lat !== "" && lat == null) {
      toast.error("La latitud no es válida.");
      return;
    }

    if (form.lng !== "" && lng == null) {
      toast.error("La longitud no es válida.");
      return;
    }

    if (lat != null && (lat < -90 || lat > 90)) {
      toast.error("La latitud debe estar entre -90 y 90.");
      return;
    }

    if (lng != null && (lng < -180 || lng > 180)) {
      toast.error("La longitud debe estar entre -180 y 180.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        contact_person: form.contact_person.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        notes: form.notes.trim(),
        lat,
        lng,
      };

      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, payload);
        toast.success("Cliente actualizado.");
      } else {
        await api.post("/clients", payload);
        toast.success("Cliente creado.");
      }

      setOpen(false);
      resetForm();
      await loadClients();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (client) => {
    const confirmed = window.confirm(
      `¿Seguro que quieres eliminar a “${client.name}”?`
    );

    if (!confirmed) return;

    setDeletingId(client.id);

    try {
      await api.delete(`/clients/${client.id}`);
      setClients((current) =>
        current.filter((item) => item.id !== client.id)
      );
      toast.success("Cliente eliminado.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };


  const loadAssets = async (client) => {
    setAssetLoading(true);
    try {
      const { data } = await api.get(
        `/assets?client_id=${encodeURIComponent(client.id)}`
      );
      setAssets(data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setAssetLoading(false);
    }
  };

  const openAssets = async (client) => {
    setAssetClient(client);
    setEditingAsset(null);
    setAssetForm(EMPTY_ASSET);
    setAssetOpen(true);
    await loadAssets(client);
  };

  const startCreateAsset = () => {
    setEditingAsset(null);
    setAssetForm(EMPTY_ASSET);
  };

  const startEditAsset = (asset) => {
    setEditingAsset(asset);
    setAssetForm({
      name: asset.name || "",
      asset_type: asset.asset_type || "Equipo",
      brand: asset.brand || "",
      model: asset.model || "",
      serial_number: asset.serial_number || "",
      location: asset.location || "",
      installation_date: asset.installation_date
        ? String(asset.installation_date).slice(0, 10)
        : "",
      warranty_until: asset.warranty_until
        ? String(asset.warranty_until).slice(0, 10)
        : "",
      status: asset.status || "activo",
      notes: asset.notes || "",
    });
  };

  const saveAsset = async (event) => {
    event.preventDefault();

    if (!assetClient || !assetForm.name.trim()) {
      toast.error("Escribe el nombre del equipo.");
      return;
    }

    setAssetSaving(true);

    try {
      const payload = {
        ...assetForm,
        client_id: assetClient.id,
        name: assetForm.name.trim(),
        brand: assetForm.brand.trim(),
        model: assetForm.model.trim(),
        serial_number: assetForm.serial_number.trim(),
        location: assetForm.location.trim(),
        notes: assetForm.notes.trim(),
        installation_date:
          assetForm.installation_date || null,
        warranty_until:
          assetForm.warranty_until || null,
      };

      if (editingAsset) {
        delete payload.client_id;
        await api.put(
          `/assets/${editingAsset.id}`,
          payload
        );
        toast.success("Equipo actualizado.");
      } else {
        await api.post("/assets", payload);
        toast.success("Equipo creado.");
      }

      setEditingAsset(null);
      setAssetForm(EMPTY_ASSET);
      await loadAssets(assetClient);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setAssetSaving(false);
    }
  };

  const removeAsset = async (asset) => {
    if (!window.confirm(`¿Eliminar el equipo "${asset.name}"?`)) {
      return;
    }

    setAssetDeletingId(asset.id);

    try {
      await api.delete(`/assets/${asset.id}`);
      toast.success("Equipo eliminado.");
      await loadAssets(assetClient);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setAssetDeletingId(null);
    }
  };

  const filteredClients = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    return clients.filter((client) => {
      const matchesQuery =
        !normalized ||
        [
          client.name,
          client.contact_person,
          client.email,
          client.phone,
          client.address,
          client.sector,
          client.notes,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalized);

      const matchesSector =
        sectorFilter === ALL_SECTORS ||
        client.sector === sectorFilter;

      return matchesQuery && matchesSector;
    });
  }, [clients, query, sectorFilter]);

  const withLocation = clients.filter(
    (client) => client.lat != null && client.lng != null
  ).length;

  const withContact = clients.filter(
    (client) =>
      Boolean(client.email || client.phone || client.contact_person)
  ).length;

  const uniqueSectors = new Set(
    clients.map((client) => client.sector).filter(Boolean)
  ).size;

  const filtersActive =
    Boolean(query.trim()) || sectorFilter !== ALL_SECTORS;

  const clearFilters = () => {
    setQuery("");
    setSectorFilter(ALL_SECTORS);
  };

  const field = (label, key, options = {}) => (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
        {label}
      </label>
      <input
        data-testid={`client-${key}`}
        type={options.type || "text"}
        step={options.step}
        value={form[key]}
        required={options.required}
        placeholder={options.placeholder || ""}
        onChange={(event) =>
          setForm({ ...form, [key]: event.target.value })
        }
        className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all placeholder:text-[#A1A1A6] focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
      />
    </div>
  );

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Clientes"
        subtitle={`${clients.length} clientes registrados`}
        action={
          <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
              setOpen(nextOpen);
              if (!nextOpen) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <button
                data-testid="new-client-btn"
                type="button"
                onClick={openCreate}
                className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499]"
              >
                <Plus className="h-4 w-4" />
                Nuevo cliente
              </button>
            </DialogTrigger>

            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl border-none bg-white p-6 shadow-2xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-bold text-[#1D1D1F]">
                  {editingClient ? "Editar cliente" : "Nuevo cliente"}
                </DialogTitle>
                <p className="mt-1 text-sm text-[#86868B]">
                  {editingClient
                    ? "Actualiza los datos de la ficha."
                    : "Añade un cliente para usarlo en avisos y órdenes."}
                </p>
              </DialogHeader>

              <form onSubmit={save} className="mt-4 space-y-5">
                {field("Nombre / Empresa", "name", {
                  required: true,
                  placeholder: "Ej. Hotel Costa Azul",
                })}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {field("Persona de contacto", "contact_person", {
                    placeholder: "Ej. Laura Martín",
                  })}

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                      Sector
                    </label>
                    <select
                      data-testid="client-sector"
                      value={form.sector}
                      onChange={(event) =>
                        setForm({ ...form, sector: event.target.value })
                      }
                      className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    >
                      {SECTORS.map((sector) => (
                        <option key={sector} value={sector}>
                          {sector}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {field("Email", "email", {
                    type: "email",
                    placeholder: "cliente@empresa.com",
                  })}
                  {field("Teléfono", "phone", {
                    type: "tel",
                    placeholder: "+34 600 000 000",
                  })}
                </div>

                {field("Dirección", "address", {
                  placeholder: "Calle, número, ciudad y código postal",
                })}

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                    Notas
                  </label>
                  <textarea
                    data-testid="client-notes"
                    value={form.notes}
                    onChange={(event) =>
                      setForm({ ...form, notes: event.target.value })
                    }
                    rows={3}
                    placeholder="Horario, accesos, observaciones o preferencias."
                    className="w-full resize-none rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all placeholder:text-[#A1A1A6] focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {field("Latitud (opcional)", "lat", {
                    type: "number",
                    step: "any",
                    placeholder: "28.9630",
                  })}
                  {field("Longitud (opcional)", "lng", {
                    type: "number",
                    step: "any",
                    placeholder: "-13.5477",
                  })}
                </div>

                <p className="rounded-xl bg-[#F5F5F7] px-4 py-3 text-xs leading-relaxed text-[#86868B]">
                  Si dejas las coordenadas vacías y escribes una dirección,
                  Navirox intentará geolocalizarla automáticamente.
                </p>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 rounded-full border border-[#D2D2D7] bg-white py-3 font-medium text-[#1D1D1F] hover:bg-[#F5F5F7]"
                  >
                    Cancelar
                  </button>

                  <button
                    data-testid="save-client"
                    type="submit"
                    disabled={saving}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white hover:bg-[#004499] disabled:opacity-60"
                  >
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    {saving
                      ? "Guardando..."
                      : editingClient
                        ? "Guardar cambios"
                        : "Guardar cliente"}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          icon={Users}
          label="Clientes registrados"
          value={clients.length}
          color="#0066CC"
        />
        <SummaryCard
          icon={MapPinned}
          label="Con ubicación"
          value={withLocation}
          color="#34C759"
        />
        <SummaryCard
          icon={Building2}
          label="Sectores distintos"
          value={uniqueSectors}
          color="#5856D6"
        />
      </section>

      <section className="mantix-card mb-6 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />
            <input
              data-testid="client-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por nombre, contacto, email, teléfono, dirección o sector..."
              className="w-full rounded-xl bg-[#F5F5F7] py-3 pl-11 pr-10 text-sm outline-none focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-[#86868B] hover:bg-white"
                aria-label="Borrar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="relative">
            <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />
            <select
              value={sectorFilter}
              onChange={(event) => setSectorFilter(event.target.value)}
              className="w-full min-w-52 appearance-none rounded-xl bg-[#F5F5F7] py-3 pl-10 pr-8 text-sm outline-none focus:ring-2 focus:ring-[#0066CC]/20 lg:w-auto"
            >
              <option value={ALL_SECTORS}>Todos los sectores</option>
              {SECTORS.map((sector) => (
                <option key={sector} value={sector}>
                  {sector}
                </option>
              ))}
            </select>
          </div>

          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-xl px-4 py-3 text-sm font-medium text-[#0066CC] hover:bg-[#0066CC]/10"
            >
              Limpiar filtros
            </button>
          )}
        </div>

        <p className="mt-3 text-xs text-[#86868B]">
          Mostrando {filteredClients.length} de {clients.length} clientes ·{" "}
          {withContact} con datos de contacto
        </p>
      </section>

      {loading ? (
        <div className="flex min-h-72 items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-[#0066CC]" />
            <p className="mt-3 text-sm text-[#86868B]">Cargando clientes...</p>
          </div>
        </div>
      ) : clients.length === 0 ? (
        <div className="mantix-card py-8">
          <EmptyState
            title="No hay clientes todavía"
            subtitle="Añade tu primer cliente para gestionar avisos y órdenes."
          />
          <div className="-mt-4 flex justify-center pb-6">
            <button
              type="button"
              onClick={openCreate}
              className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#004499]"
            >
              <Plus className="h-4 w-4" />
              Crear primer cliente
            </button>
          </div>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="mantix-card flex min-h-72 flex-col items-center justify-center p-8 text-center">
          <Search className="h-9 w-9 text-[#B0B0B5]" />
          <h3 className="mt-4 font-display text-lg font-semibold text-[#1D1D1F]">
            No hay resultados
          </h3>
          <p className="mt-1 text-sm text-[#86868B]">
            Prueba a cambiar la búsqueda o el sector.
          </p>
          <button
            type="button"
            onClick={clearFilters}
            className="mt-5 rounded-full bg-[#0066CC]/10 px-5 py-2.5 text-sm font-medium text-[#0066CC]"
          >
            Limpiar filtros
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredClients.map((client) => {
            const isDeleting = deletingId === client.id;

            return (
              <article
                key={client.id}
                data-testid={`client-card-${client.id}`}
                className="mantix-card group p-5 transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0066CC]/10">
                    <Building2 className="h-5 w-5 text-[#0066CC]" />
                  </div>

                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => openEdit(client)}
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                      aria-label={`Editar ${client.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>

                    <button
                      data-testid={`delete-client-${client.id}`}
                      type="button"
                      onClick={() => remove(client)}
                      disabled={isDeleting}
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30] disabled:opacity-50"
                      aria-label={`Eliminar ${client.name}`}
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <h3 className="font-display text-lg font-semibold text-[#1D1D1F]">
                  {client.name}
                </h3>

                {client.contact_person && (
                  <p className="mt-1 text-sm text-[#86868B]">
                    Contacto: {client.contact_person}
                  </p>
                )}

                <span className="mt-3 inline-block rounded-full bg-[#F5F5F7] px-3 py-1 text-xs font-medium text-[#86868B]">
                  {client.sector || "Sin sector"}
                </span>

                <div className="mt-5 space-y-2.5 text-sm text-[#86868B]">
                  {client.email && (
                    <a
                      href={`mailto:${client.email}`}
                      className="flex items-center gap-2 break-all hover:text-[#0066CC]"
                    >
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      {client.email}
                    </a>
                  )}

                  {client.phone && (
                    <a
                      href={`tel:${client.phone}`}
                      className="flex items-center gap-2 hover:text-[#0066CC]"
                    >
                      <Phone className="h-3.5 w-3.5 shrink-0" />
                      {client.phone}
                    </a>
                  )}

                  {client.address && (
                    <p className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span className="line-clamp-2">{client.address}</span>
                    </p>
                  )}
                </div>

                {client.notes && (
                  <div className="mt-5 rounded-xl bg-[#F8F8FA] p-3">
                    <p className="line-clamp-3 text-xs leading-relaxed text-[#86868B]">
                      {client.notes}
                    </p>
                  </div>
                )}

                <div className="mt-5 flex items-center justify-between border-t border-[#E8E8ED] pt-4 text-xs text-[#86868B]">
                  <span>
                    {client.lat != null && client.lng != null
                      ? "Ubicación disponible"
                      : "Sin coordenadas"}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => openAssets(client)}
                      className="flex items-center gap-1.5 font-medium text-[#0066CC] hover:underline"
                    >
                      <Boxes className="h-3.5 w-3.5" />
                      Equipos
                    </button>
                    <button
                      type="button"
                      onClick={() => openEdit(client)}
                      className="font-medium text-[#0066CC] hover:underline"
                    >
                      Ver ficha
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
      <Dialog
        open={assetOpen}
        onOpenChange={(value) => {
          setAssetOpen(value);
          if (!value) {
            setAssetClient(null);
            setAssets([]);
            setEditingAsset(null);
            setAssetForm(EMPTY_ASSET);
          }
        }}
      >
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto rounded-3xl border-none bg-white p-6 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold text-[#1D1D1F]">
              Equipos de {assetClient?.name}
            </DialogTitle>
            <p className="mt-1 text-sm text-[#86868B]">
              Activos, maquinaria e historial de mantenimiento del cliente.
            </p>
          </DialogHeader>

          <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
            <form
              onSubmit={saveAsset}
              className="space-y-4 rounded-2xl bg-[#F8F8FA] p-5"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-semibold">
                  {editingAsset ? "Editar equipo" : "Nuevo equipo"}
                </h3>
                {editingAsset && (
                  <button
                    type="button"
                    onClick={startCreateAsset}
                    className="text-xs font-medium text-[#0066CC]"
                  >
                    Crear otro
                  </button>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">
                  Nombre
                </label>
                <input
                  value={assetForm.name}
                  onChange={(event) =>
                    setAssetForm({
                      ...assetForm,
                      name: event.target.value,
                    })
                  }
                  required
                  placeholder="Aire acondicionado planta 2"
                  className="w-full rounded-xl bg-white px-4 py-3 outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">
                    Tipo
                  </label>
                  <input
                    value={assetForm.asset_type}
                    onChange={(event) =>
                      setAssetForm({
                        ...assetForm,
                        asset_type: event.target.value,
                      })
                    }
                    className="w-full rounded-xl bg-white px-4 py-3 outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">
                    Estado
                  </label>
                  <select
                    value={assetForm.status}
                    onChange={(event) =>
                      setAssetForm({
                        ...assetForm,
                        status: event.target.value,
                      })
                    }
                    className="w-full rounded-xl bg-white px-4 py-3 outline-none"
                  >
                    <option value="activo">Activo</option>
                    <option value="mantenimiento">Mantenimiento</option>
                    <option value="averiado">Averiado</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input
                  value={assetForm.brand}
                  onChange={(event) =>
                    setAssetForm({
                      ...assetForm,
                      brand: event.target.value,
                    })
                  }
                  placeholder="Marca"
                  className="rounded-xl bg-white px-4 py-3 outline-none"
                />
                <input
                  value={assetForm.model}
                  onChange={(event) =>
                    setAssetForm({
                      ...assetForm,
                      model: event.target.value,
                    })
                  }
                  placeholder="Modelo"
                  className="rounded-xl bg-white px-4 py-3 outline-none"
                />
              </div>

              <input
                value={assetForm.serial_number}
                onChange={(event) =>
                  setAssetForm({
                    ...assetForm,
                    serial_number: event.target.value,
                  })
                }
                placeholder="Número de serie"
                className="w-full rounded-xl bg-white px-4 py-3 outline-none"
              />

              <input
                value={assetForm.location}
                onChange={(event) =>
                  setAssetForm({
                    ...assetForm,
                    location: event.target.value,
                  })
                }
                placeholder="Ubicación dentro del cliente"
                className="w-full rounded-xl bg-white px-4 py-3 outline-none"
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-[#86868B]">
                    Instalación
                  </label>
                  <input
                    type="date"
                    value={assetForm.installation_date}
                    onChange={(event) =>
                      setAssetForm({
                        ...assetForm,
                        installation_date: event.target.value,
                      })
                    }
                    className="w-full rounded-xl bg-white px-3 py-3 outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-[#86868B]">
                    Garantía
                  </label>
                  <input
                    type="date"
                    value={assetForm.warranty_until}
                    onChange={(event) =>
                      setAssetForm({
                        ...assetForm,
                        warranty_until: event.target.value,
                      })
                    }
                    className="w-full rounded-xl bg-white px-3 py-3 outline-none"
                  />
                </div>
              </div>

              <textarea
                rows={3}
                value={assetForm.notes}
                onChange={(event) =>
                  setAssetForm({
                    ...assetForm,
                    notes: event.target.value,
                  })
                }
                placeholder="Notas del equipo"
                className="w-full resize-none rounded-xl bg-white px-4 py-3 outline-none"
              />

              <button
                type="submit"
                disabled={assetSaving}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white disabled:opacity-60"
              >
                {assetSaving && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {editingAsset
                  ? "Guardar cambios"
                  : "Crear equipo"}
              </button>
            </form>

            <div>
              {assetLoading ? (
                <div className="flex min-h-64 items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-[#0066CC]" />
                </div>
              ) : assets.length === 0 ? (
                <div className="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-[#D2D2D7] text-center">
                  <Boxes className="h-9 w-9 text-[#B0B0B5]" />
                  <p className="mt-3 font-medium">
                    Este cliente no tiene equipos
                  </p>
                  <p className="mt-1 text-sm text-[#86868B]">
                    Crea el primero usando el formulario.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {assets.map((asset) => (
                    <article
                      key={asset.id}
                      className="rounded-2xl border border-[#E8E8ED] p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0066CC]/10">
                            <Wrench className="h-5 w-5 text-[#0066CC]" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-semibold text-[#1D1D1F]">
                              {asset.name}
                            </h4>
                            <p className="mt-0.5 text-xs text-[#86868B]">
                              {[asset.brand, asset.model]
                                .filter(Boolean)
                                .join(" · ") || asset.asset_type}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => startEditAsset(asset)}
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeAsset(asset)}
                            disabled={assetDeletingId === asset.id}
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30]"
                          >
                            {assetDeletingId === asset.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                        <div className="rounded-xl bg-[#F5F5F7] p-3">
                          <p className="text-[#86868B]">Serie</p>
                          <p className="mt-1 font-medium">
                            {asset.serial_number || "—"}
                          </p>
                        </div>
                        <div className="rounded-xl bg-[#F5F5F7] p-3">
                          <p className="text-[#86868B]">Ubicación</p>
                          <p className="mt-1 font-medium">
                            {asset.location || "—"}
                          </p>
                        </div>
                        <div className="rounded-xl bg-[#F5F5F7] p-3">
                          <p className="text-[#86868B]">Estado</p>
                          <p className="mt-1 font-medium capitalize">
                            {asset.status}
                          </p>
                        </div>
                        <div className="rounded-xl bg-[#F5F5F7] p-3">
                          <p className="text-[#86868B]">Intervenciones</p>
                          <p className="mt-1 font-medium">
                            {asset.orders_count || 0}
                          </p>
                        </div>
                      </div>

                      {asset.history?.length > 0 && (
                        <div className="mt-4 rounded-xl bg-[#F8F8FA] p-3">
                          <p className="flex items-center gap-2 text-xs font-semibold text-[#1D1D1F]">
                            <History className="h-3.5 w-3.5 text-[#0066CC]" />
                            Últimas intervenciones
                          </p>
                          <div className="mt-2 space-y-1.5">
                            {asset.history.slice(0, 3).map((item) => (
                              <div
                                key={item.id}
                                className="flex items-center justify-between gap-3 text-xs"
                              >
                                <span className="truncate text-[#86868B]">
                                  {item.number} · {item.title}
                                </span>
                                <span className="shrink-0 capitalize text-[#1D1D1F]">
                                  {item.status?.replace("_", " ")}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}